import { useTranslation } from "react-i18next"

import { AuthenticatedPageLayout } from "@/components/authenticated-page-layout"
import { LanguageSwitcher } from "@/components/nav/language-switcher"
import { PageCard } from "@/components/page-card"
import { useUserSettings } from "@/contexts"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { TextField } from "@/nzds/inputs/text-field"
import { useFlags } from "@/services/feature"

import { MenuItem, Stack } from "@mui/material"

/**
 * A component that renders the Settings content
 *
 * @example
 * return (
 *   <Settings />
 * )
 */

export const Settings = () => {
  const { i18n, t } = useTranslation(["common", "user-settings"])

  const flags = useFlags()

  const { currency, supportedCurrencies, unitsSystem } = useUserSettings()

  return (
    <AuthenticatedPageLayout title={t("moduleTitle.userSettings")}>
      <PageCard>
        <Stack
          sx={{
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          {flags.user_settings_language ? (
            <LanguageSwitcher id="language-select" variant="stacked" />
          ) : (
            <ReadonlyField
              label={t("languageLabel", { ns: "user-settings" })}
              value={t(`language.${i18n.resolvedLanguage}`)}
            />
          )}

          {flags.user_settings_currency ? (
            <TextField
              id="currency-select"
              label={t("currencyLabel", { ns: "user-settings" })}
              margin="dense"
              select
              slotProps={{
                select: {
                  inputProps: {
                    "data-testid": "currency-select",
                  },
                },
              }}
              value={currency}
              variant="stacked"
            >
              {supportedCurrencies.map((supportedCurrency) => (
                <MenuItem
                  key={supportedCurrency}
                  data-testid={`currency-select-option-${supportedCurrency}`}
                  value={supportedCurrency}
                >
                  {t(`currency.${supportedCurrency}`)} ({supportedCurrency})
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <ReadonlyField
              label={t("currencyLabel", { ns: "user-settings" })}
              value={`${t(`currency.${currency}`)} (${currency})`}
            />
          )}

          <ReadonlyField
            label={t("unitsLabel", { ns: "user-settings" })}
            value={t(`language.${unitsSystem}`)}
          />
        </Stack>
      </PageCard>
    </AuthenticatedPageLayout>
  )
}
